// Patient Records → Admissions: this patient's in-patient stays at the active
// branch, open and discharged, with the state of each stay's paperwork and
// a way into its Case Sheet. This is where MRD and insurance staff come when
// a claim or a records request arrives long after discharge.
//
// Admissions are branch-scoped (routes/ip.js, guards.js returns 404 for
// another branch), while this record is hospital-wide — so the tab says which
// branch it is showing rather than implying it is the complete list.
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable';
import { useAuth } from '@/hooks/useAuth';
import { GenerateCaseSheetDialog } from '@/features/ip-case-sheet/GenerateCaseSheetDialog';
import { useAdmissionList } from './hooks';
import type { AdmissionListRow } from './api';
import { CaseSheetStatusBadge, SummaryStatusBadge, dischargeTypeLabel, fmtDate, stayDays } from './ui';

interface Props {
  patientId: number;
  uhid: string;
  /** Opens the admission's Case Sheet in the IP module. Omitted → no Open button. */
  onOpenAdmission?: (row: AdmissionListRow) => void;
}

export function PatientAdmissionsTab({ patientId, uhid, onOpenAdmission }: Props) {
  const { user } = useAuth();
  const branchName = (user as { active_branch?: { name?: string } } | null)?.active_branch?.name;
  // Sized for one patient: nobody has hundreds of stays at one branch.
  const list = useAdmissionList({ patientId, page: 1, limit: 100 });
  const rows = list.data?.data ?? [];
  const [generateFor, setGenerateFor] = useState<number | null>(null);

  const columns: DataTableColumn<AdmissionListRow>[] = [
    {
      key: 'stay', header: 'Stay',
      render: (r) => (
        <div className="text-[12.5px]">
          <div className="font-semibold text-ink">
            {fmtDate(r.admitted_at)} → {r.discharged_at ? fmtDate(r.discharged_at) : 'now'}
            {r.ip_number && <span className="ml-1.5 font-normal text-ink-muted">· {r.ip_number}</span>}
          </div>
          <div className="text-[11.5px] text-ink-muted">
            {stayDays(r)} day{stayDays(r) === 1 ? '' : 's'} · {r.ward_name} · Bed {r.bed_number}
            {r.admitting_doctor ? ` · ${r.admitting_doctor}` : ''}
          </div>
          {r.provisional_diagnosis && <div className="text-[11.5px] text-ink-muted">Dx: {r.provisional_diagnosis}</div>}
        </div>
      ),
    },
    {
      key: 'status', header: 'Status',
      render: (r) => r.status === 'admitted'
        ? <Badge variant="info">Admitted</Badge>
        : <Badge variant="neutral">Discharged{dischargeTypeLabel(r.discharge_type) && r.discharge_type !== 'normal' ? ` · ${dischargeTypeLabel(r.discharge_type)}` : ''}</Badge>,
    },
    { key: 'summary', header: 'Discharge summary', render: (r) => <SummaryStatusBadge status={r.discharge_summary_status} /> },
    { key: 'casesheet', header: 'Case sheet', render: (r) => <CaseSheetStatusBadge row={r} /> },
    {
      key: 'actions', header: '', align: 'right',
      render: (r) => (
        <div className="flex justify-end gap-1.5">
          {onOpenAdmission && (
            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onOpenAdmission(r); }}>
              Open case sheet
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setGenerateFor(r.id); }}>
            📄 Generate
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-2.5 text-xs text-ink-muted">
        In-patient stays at {branchName ? <b>{branchName}</b> : 'this branch'}. Stays at another branch appear after you switch to that branch.
      </div>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        onRowClick={onOpenAdmission}
        isLoading={list.isLoading}
        isError={list.isError}
        errorMessage={list.error instanceof Error ? list.error.message : undefined}
        onRetry={() => list.refetch()}
        emptyLabel="No admissions at this branch"
      />
      {generateFor != null && (
        <GenerateCaseSheetDialog
          admissionId={generateFor}
          uhid={uhid}
          open
          onOpenChange={(o) => { if (!o) { setGenerateFor(null); list.refetch(); } }}
        />
      )}
    </div>
  );
}
